import React, { useMemo, useState } from 'react';
import { Package, SearchX } from 'lucide-react';
import { Alert, Button, EmptyState, PageHeader, SearchField, SegmentedTabs, SegmentedTabItem } from '@/components/common';
import OrderCard from '@/components/customer-dashboard/OrderCard';
import { useCustomerOrders } from '@/hooks/useCustomerProducts';

type Filter = 'all' | 'arriving' | 'delivered';

const CardSkeleton: React.FC = () => (
  <div aria-hidden className="tw-flex tw-animate-pulse tw-flex-col tw-gap-[14px] tw-rounded-2xl tw-border tw-border-ds-border-default tw-bg-ds-surface tw-p-5">
    <div className="tw-flex tw-items-center tw-gap-[14px]">
      <span className="tw-h-14 tw-w-14 tw-rounded-xl tw-bg-ds-subtle" />
      <span className="tw-h-4 tw-w-1/2 tw-rounded tw-bg-ds-subtle" />
    </div>
    <span className="tw-h-3 tw-w-2/3 tw-rounded tw-bg-ds-subtle" />
    <span className="tw-h-9 tw-rounded-[10px] tw-bg-ds-subtle" />
  </div>
);

/** T2 Orders: everything bought with the customer's mobile number. */
const OrdersPage: React.FC = () => {
  const { orders, loading, error, reload } = useCustomerOrders();
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');

  const arriving = orders.filter((o) => o.state === 'on_the_way');
  const tabs: SegmentedTabItem<Filter>[] = [
    { value: 'all', label: `All · ${orders.length}` },
    { value: 'arriving', label: `Arriving · ${arriving.length}` },
    { value: 'delivered', label: `Delivered · ${orders.length - arriving.length}` },
  ];

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders
      .filter((o) => (filter === 'all' ? true : filter === 'arriving' ? o.state === 'on_the_way' : o.state !== 'on_the_way'))
      .filter((o) => !q || o.title.toLowerCase().includes(q) || o.orderLabel?.toLowerCase().includes(q));
  }, [orders, filter, query]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader title="Orders" subtitle="Everything you bought with your mobile number." />

      <div className="tw-flex tw-flex-col tw-gap-3 md:tw-flex-row md:tw-items-center md:tw-justify-between">
        <SegmentedTabs items={tabs} value={filter} onChange={setFilter} label="Filter orders" />
        <SearchField value={query} onChange={setQuery} placeholder="Search by product or order ID" className="md:tw-w-[280px]" />
      </div>

      {error ? (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <Alert tone="danger" title="Something went wrong">
            {error}
          </Alert>
          <Button variant="secondary" className="tw-self-start" onClick={reload}>
            Try again
          </Button>
        </div>
      ) : loading ? (
        <div aria-busy className="tw-grid tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-5">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : orders.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No orders yet"
          description="Orders you place with brands that offer Protega protection show up here automatically."
        />
      ) : visible.length === 0 ? (
        <EmptyState icon={SearchX} title="No matching orders" description="Try a different product name or order ID." />
      ) : (
        <div className="tw-grid tw-items-start tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-5">
          {visible.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
